import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import * as React from "react";
import { useState } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
    appendMidnightTimeToDate,
    cn,
    formatDate,
    formatDateWithoutTime,
    getThirdPartyTypeFromUrl,
    removeEnumUnderscore,
} from "../../../../../utils/helpers";
import {
    IClientInsuranceAttachment,
    IClientInsuranceRecords,
} from "../../../types/clients.types";
import {
    calculateFees,
    getMessageForFees,
} from "../../../../../utils/helpers/fees/fees";
import ClientSingleFee from "../../ClientFeesList/ClientSingleFee/ClientSingleFee";
import DeleteIcon from "../../../../../icons/Delete";
import InfoIcon from "../../../../../icons/Info";
import useToast from "../../../../../hooks/useToast";
import {
    useDeleteClientInsuranceAttachment,
    useDeleteClientInsuranceInformation,
} from "../../../hooks/clients.mutations";
import { useDoesUserHavePermission } from "../../../../../hooks/permissions";
import { UserPermisions } from "../../../../../utils/types/user";
import Progress from "../../../../../components/Progress/Progress";
import EditInsuranceDetails from "../../Insurance/InsuranceDetails/EditInsuranceDetails/EditInsuranceDetails";
import FilePicker from "../../../../../components/FilePicker/FilePicker";
import { SupportedUploadMethod } from "../../../../../utils/types/filepicker";
import InsuranceDocument from "../../Insurance/InsuranceDetails/InsuranceDocument/InsuranceDocument";
import EditClientFeeForm from "../../ClientFeesList/EditClientFeeForm/EditClientFeeForm";
import TimesIcon from "../../../../../icons/Times";
import ListState from "../../../../../components/ListState/ListState";

interface ClientInsuranceRecordsItemProps {
    record: IClientInsuranceRecords;
    isFirst?: boolean;
}

enum FormTypes {
    EDIT_INSURANCE = "edit-insurance",
    ADD_DOCUMENT = "add-document",
}

type SubmitTypes = "save" | "save-continue";

export default function ClientInsuranceRecordsItem({
    record,
    isFirst = false,
}: ClientInsuranceRecordsItemProps) {
    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    // Local component states
    const [formInView, setFormInView] = React.useState<FormTypes | null>(null);
    const [progressStep, setProgressStep] = React.useState(
        FormTypes.EDIT_INSURANCE
    );
    const [submitType, setSubmitType] = React.useState<SubmitTypes | null>(
        null
    );
    const [documentToDeleteId, setDocumentToDeleteId] = React.useState<
        string | null
    >(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] =
        React.useState(false);
    const [attachmentInView, setAttachmentInView] =
        React.useState<IClientInsuranceAttachment | null>(null);

    const [showInsuranceDetails, setShowInsuranceDetails] = useState(false);
    const [showFeesHistory, setShowFeesHistory] = useState(isFirst || false);
    const [showEditFeesDialog, setShowEditFeesDialog] = useState(false);
    const [
        showDeleteInsuranceConfirmation,
        setShowDeleteInsuranceConfirmation,
    ] = React.useState(false);

    // Query client
    const queryClient = useQueryClient();

    // Hook for toast
    const { toast } = useToast();

    // To determine if mutation is currently running, returns 0 || 1
    const isMutating = useIsMutating(["edit-insurance", clientId]);

    const isEditFeeMutating = useIsMutating(["edit-fee"]);

    // Delete client document hook
    const deleteDocument = useDeleteClientInsuranceAttachment(
        clientId,
        documentToDeleteId || ""
    );

    const deleteInsurance = useDeleteClientInsuranceInformation(clientId);

    const canDeleteInsurance = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_INSURANCE_WRITE,
        UserPermisions.CLIENT_INFO_ALL_WRITE,
    ]);

    const handleDocumentinView = (
        attachment: IClientInsuranceAttachment | null
    ) => {
        const thirdPartyType = attachment
            ? getThirdPartyTypeFromUrl(attachment.url)
            : null;
        if (attachment && thirdPartyType) {
            window.open(attachment.url, "_blank", "noopener,noreferrer");
            return;
        }
        setAttachmentInView(attachment);
    };

    const handleSetFormInView = (form: FormTypes | null) => {
        setFormInView(form);
    };

    // Function for deleting document
    const handleDeleteDocument = () => {
        deleteDocument.mutate(null, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({
                    queryKey: [clientId, `insurance`],
                });

                queryClient.invalidateQueries({
                    queryKey: [clientId, `insurance-fees`],
                });

                toast({
                    mode: "success",
                    message:
                        res.message ||
                        "Insurance attachment deleted successfully!",
                });
                setDocumentToDeleteId(null);
                setShowDeleteConfirmation(false);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete insurance attachment at this time",
                });
            },
        });
    };

    const handleDeleteInsurance = () => {
        deleteInsurance.mutate(
            { insurance_ids: [record.insurance_id] },
            {
                onSuccess: (res) => {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `insurance`],
                    });

                    queryClient.invalidateQueries({
                        queryKey: [clientId, `insurance-fees`],
                    });

                    toast({
                        mode: "success",
                        message:
                            res.message || "Insurance deleted successfully!",
                    });
                    setShowDeleteInsuranceConfirmation(false);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete insurance at this time",
                    });
                },
            }
        );
    };

    const showFeeEffectiveDate =
        record.effective_start_date ||
        record.start_date ||
        record.fees[0]?.fee_effective_start_date ||
        record.fees[0]?.start_date;

    const getEffectiveDate = () => {
        if (
            record.name === "Self-Pay" &&
            record.fees[0]?.fee_effective_start_date
        ) {
            return `${formatDateWithoutTime(
                record.fees[0].fee_effective_start_date
            )} - ${
                record.fees[0]?.fee_effective_end_date
                    ? formatDateWithoutTime(
                          record.fees[0].fee_effective_end_date
                      )
                    : "present"
            }`;
        }

        const effectiveStartDate =
            record.effective_start_date ||
            record.start_date ||
            record.fees[0]?.fee_effective_start_date ||
            record.fees[0]?.start_date;
        const effectiveEndDate =
            record.effective_end_date ||
            record.end_date ||
            record.fees[0]?.fee_effective_end_date ||
            record.fees[0]?.end_date;
        const startDate = effectiveStartDate
            ? formatDateWithoutTime(effectiveStartDate)
            : "--";
        const endDate = effectiveEndDate
            ? formatDateWithoutTime(effectiveEndDate)
            : "present";

        return `${startDate} - ${endDate}`;
    };

    const getPolicyDate = () => {
        const startDate = record.policy_effective_start_date
            ? formatDateWithoutTime(record.policy_effective_start_date)
            : "--";
        const endDate = record.policy_effective_end_date
            ? formatDateWithoutTime(record.policy_effective_end_date)
            : "--";
        return `${startDate} - ${endDate}`;
    };

    const getAccumulationDate = () => {
        const startDate = record.accumulations_start_date
            ? formatDate(record.accumulations_start_date, true)
            : "--";
        const endDate = record.accumulations_end_date
            ? formatDate(record.accumulations_end_date, true)
            : "--";
        return `${startDate} - ${endDate}`;
    };

    const highlightedFeesDisplay = React.useMemo(() => {
        const currentFee = record.fees[0];
        if (!currentFee) return null;

        const seen = new Set<string>();

        return currentFee.providers.reduce<
            {
                label: string;
                value: string | null;
                type: string;
                feesCalculated: ReturnType<typeof calculateFees> | null;
                providerName: string;
                providerId: string;
                isOverride: boolean;
            }[]
        >((acc, provider) => {
            if (!provider.applicable_fee_value || !provider.applicable_fee_type)
                return acc;

            const isOverride = currentFee.fee_values.some(
                (fv) =>
                    fv.provider?.provider_id === provider.provider_id &&
                    fv.value === provider.applicable_fee_value &&
                    fv.type === provider.applicable_fee_type
            );

            const key = isOverride
                ? `${provider.provider_id}:${provider.applicable_fee_type}:${provider.applicable_fee_source}`
                : `general:${provider.applicable_fee_type}`;

            if (seen.has(key)) return acc;
            seen.add(key);

            const feeType = provider.applicable_fee_type;
            acc.push({
                label: removeEnumUnderscore(feeType),
                value: provider.applicable_fee_value ?? null,
                type: feeType,
                feesCalculated: provider.applicable_fee_value
                    ? calculateFees(provider.applicable_fee_value)
                    : null,
                providerName: `${provider.first_name} ${provider.last_name}`,
                providerId: provider.provider_id,
                isOverride,
            });

            return acc;
        }, []);
    }, [record.fees]);

    const groupedHighlightedFees = React.useMemo(() => {
        if (!highlightedFeesDisplay) return [];

        const grouped = new Map<
            string,
            {
                label: string;
                type: string;
                entries: {
                    value: string | null;
                    providerId?: string;
                    providerName?: string;
                    isOverride: boolean;
                    feesCalculated: ReturnType<typeof calculateFees>;
                }[];
            }
        >();

        highlightedFeesDisplay.forEach((fee) => {
            const existing = grouped.get(fee.type);
            const entry = {
                value: fee.value as string,
                providerId: fee.providerId,
                providerName: fee.providerName,
                isOverride: fee.isOverride,
                feesCalculated: fee.feesCalculated,
            };

            if (existing) {
                existing.entries.push(entry);
            } else {
                grouped.set(fee.type, {
                    label: fee.label,
                    type: fee.type,
                    entries: [entry],
                });
            }
        });

        grouped.forEach((group) => {
            group.entries.sort(
                (a, b) => Number(a.isOverride) - Number(b.isOverride)
            );
        });

        return [...grouped.values()];
    }, [highlightedFeesDisplay]);

    const formatDateString = (dateStr: string) => {
        if (dateStr.includes("T")) {
            return formatDate(dateStr, true);
        }
        return formatDate(appendMidnightTimeToDate(dateStr), true);
    };

    return (
        <div className="rounded-md border border-strokedark bg-[#E8F1F4] text-black p-[--padding-card-y]">
            <Dialog
                open={
                    formInView === FormTypes.EDIT_INSURANCE ||
                    formInView === FormTypes.ADD_DOCUMENT
                }
            >
                <DialogContent
                    showFooter={formInView === FormTypes.EDIT_INSURANCE}
                    variant="center"
                    title={
                        formInView === FormTypes.EDIT_INSURANCE
                            ? "Edit insurance"
                            : "Add Document"
                    }
                    submitBtnFormValue="edit-insurance"
                    cancelBtnFormValue="edit-insurance"
                    saveText="Save and continue"
                    cancelText="Save changes"
                    isCancelBtnDisabled={isMutating > 0}
                    isSubmitBtnDisabled={isMutating > 0}
                    handleCloseDialog={() => handleSetFormInView(null)}
                    onSaveClick={() => {
                        setSubmitType("save-continue");
                    }}
                    canCancelButtonSubmit
                    onCancelClick={() => setSubmitType("save")}
                >
                    <div>
                        <Progress
                            steps={Object.values(FormTypes)}
                            currentStep={progressStep}
                            className="top-12"
                        />
                        <div className="pt-5">
                            {formInView === FormTypes.EDIT_INSURANCE && (
                                <EditInsuranceDetails
                                    insuranceDetail={record}
                                    onFormSubmit={() => {
                                        if (submitType === "save-continue") {
                                            setProgressStep(
                                                FormTypes.ADD_DOCUMENT
                                            );
                                            handleSetFormInView(
                                                FormTypes.ADD_DOCUMENT
                                            );
                                        } else setFormInView(null);
                                    }}
                                />
                            )}
                            {formInView === FormTypes.ADD_DOCUMENT && (
                                <FilePicker
                                    name="filepicker"
                                    uploadMethods={[
                                        {
                                            method: SupportedUploadMethod.manualupload,
                                        },
                                        {
                                            method: SupportedUploadMethod.googledrive,
                                        },
                                        {
                                            method: SupportedUploadMethod.dropbox,
                                        },
                                    ]}
                                    uploadType="insurance-file"
                                    insuranceId={record.insurance_id}
                                    successCallback={() => {
                                        handleSetFormInView(null);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <AlertDialog open={showDeleteConfirmation}>
                <AlertDialogContent
                    title=" Are you sure you want to delete attachment?"
                    description=" Are you sure you want to delete this attachment?
                    This action cannot be reversed."
                    saveText="Delete"
                    cancelText="Cancel"
                    onSaveClick={handleDeleteDocument}
                    isSaveBtnDisabled={deleteDocument.isLoading}
                    isCancelBtnDisabled={deleteDocument.isLoading}
                    onCancelClick={() => {
                        setDocumentToDeleteId(null);
                        setShowDeleteConfirmation(false);
                    }}
                    isDeleting
                />
            </AlertDialog>
            <AlertDialog open={showDeleteInsuranceConfirmation}>
                <AlertDialogContent
                    title="Delete Insurance"
                    description="Are you sure you want to delete this insurance? This action cannot be reversed."
                    saveText="Delete"
                    cancelText="Cancel"
                    onSaveClick={handleDeleteInsurance}
                    isSaveBtnDisabled={deleteInsurance.isLoading}
                    isCancelBtnDisabled={deleteInsurance.isLoading}
                    onCancelClick={() => {
                        setShowDeleteInsuranceConfirmation(false);
                    }}
                    isDeleting
                />
            </AlertDialog>
            <Dialog
                open={Boolean(attachmentInView) && attachmentInView !== null}
            >
                <DialogContent
                    title="View Document"
                    handleCloseDialog={() => handleDocumentinView(null)}
                    variant="drawer"
                    showFooter={false}
                    wrapperClass="w-[900px]"
                >
                    <div>
                        {attachmentInView && (
                            <InsuranceDocument
                                attachmentInView={attachmentInView}
                                attachmentUrl={attachmentInView.url}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={showEditFeesDialog}>
                <DialogContent
                    variant="center"
                    title="Edit fee"
                    classNames="!px-0 !pt-0"
                    wrapperClass="w-[790px]"
                    handleCloseDialog={() => setShowEditFeesDialog(false)}
                    submitBtnFormValue="edit-fees"
                    isSubmitBtnDisabled={isEditFeeMutating > 0}
                    isCancelBtnDisabled={isEditFeeMutating > 0}
                >
                    <EditClientFeeForm
                        onFormSubmit={() => {
                            setShowEditFeesDialog(false);
                        }}
                        fee={record.fees[0]}
                        insuranceId={
                            record.name !== "Self-Pay"
                                ? record.insurance_id
                                : undefined
                        }
                    />
                </DialogContent>
            </Dialog>
            <div className="w-full flex flex-col gap-y-2">
                <div className="flex items-center justify-between px-[--padding-card-x] py-[--padding-card-y] gap-x-2 border-b border-[var(--color-strokedark)]">
                    <div className="flex items-center gap-x-[--spacing-header] text-dark">
                        <div className="flex flex-col items-start justify-center gap-y-1">
                            <div className="flex items-center gap-x-2 text-title font-semibold">
                                {record.name}
                                {record.status === "active" && (
                                    <Badge content="Active" type="success" />
                                )}
                            </div>
                            {record.insurance_member_id ? (
                                <span className="text-caption font-regular">
                                    MEMBER ID: {record.insurance_member_id}
                                </span>
                            ) : null}
                        </div>

                        {showFeeEffectiveDate ? (
                            <div className="flex flex-col items-start justify-center gap-y-1 text-caption">
                                <span className="uppercase font-semibold">
                                    EFFECTIVE DATE
                                </span>
                                <span className="text-caption font-regular">
                                    {getEffectiveDate()}
                                </span>
                            </div>
                        ) : null}

                        {record.policy_effective_start_date ? (
                            <div className="flex flex-col items-start justify-center gap-y-1 text-caption">
                                <span className="uppercase font-semibold">
                                    POLICY DATE
                                </span>
                                <span className="text-caption font-regular">
                                    {getPolicyDate()}
                                </span>
                            </div>
                        ) : null}

                        {record.accumulations_start_date ? (
                            <div className="flex flex-col items-start justify-center gap-y-1 text-caption">
                                <span className="uppercase font-semibold">
                                    ACCUMULATION DATE
                                </span>
                                <span className="text-caption font-regular">
                                    {getAccumulationDate()}
                                </span>
                            </div>
                        ) : null}

                        {record.last_verified ||
                        record.fees[0]?.fee_last_verified ? (
                            <div className="flex flex-col items-start justify-center gap-y-1 text-caption">
                                <span className="uppercase font-semibold">
                                    LAST VERIFIED
                                </span>
                                <span className="text-caption font-regular">
                                    {record.last_verified ||
                                    record.fees[0]?.fee_last_verified
                                        ? formatDateString(
                                              record.last_verified ||
                                                  (record.fees[0]
                                                      ?.fee_last_verified as string)
                                          )
                                        : "--"}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex items-center gap-x-2">
                        {record.name !== "Self-Pay" && (
                            <Button
                                variant="normal"
                                className={cn(
                                    "rounded-md px-3 py-2 flex items-center justify-center font-medium",
                                    showInsuranceDetails
                                        ? "bg-[rgba(26,115,141,0.1)] text-primary border-none"
                                        : "text-dark [border:1px_solid_var(--color-primary)] bg-transparent"
                                )}
                                onClick={() => {
                                    setShowInsuranceDetails(true);
                                    setShowFeesHistory(false);
                                }}
                            >
                                Insurance
                            </Button>
                        )}
                        <Button
                            variant="normal"
                            className={cn(
                                "rounded-md px-3 py-2 flex items-center justify-center font-medium",
                                showFeesHistory
                                    ? "bg-[rgba(26,115,141,0.1)] text-primary border-none"
                                    : "text-dark [border:1px_solid_var(--color-primary)] bg-transparent"
                            )}
                            onClick={() => {
                                setShowFeesHistory(true);
                                setShowInsuranceDetails(false);
                            }}
                        >
                            Fees
                        </Button>
                        {(showInsuranceDetails || showFeesHistory) && (
                            <ActionButton
                                variant="normal"
                                size="auto"
                                className="rounded-full size-8 flex items-center justify-center bg-white text-text border border-[--color-stroke]"
                                actionTitle="Close"
                                onClick={() => {
                                    setShowInsuranceDetails(false);
                                    setShowFeesHistory(false);
                                }}
                            >
                                <TimesIcon className="stroke-dark" />
                            </ActionButton>
                        )}
                        {canDeleteInsurance ? (
                            <ActionButton
                                size="auto"
                                variant="can-hover"
                                aria-label="delete button"
                                onClick={() => {
                                    setShowDeleteInsuranceConfirmation(true);
                                }}
                                actionTitle="Delete insurance"
                            >
                                <DeleteIcon className="stroke-black" />
                            </ActionButton>
                        ) : null}
                    </div>
                </div>
                {!showFeesHistory &&
                    !showInsuranceDetails &&
                    groupedHighlightedFees.length > 0 && (
                        <div className="flex items-center gap-x-2 bg-gray-100 px-[--padding-card-x] py-[--padding-card-y]">
                            <span className="text-base font-semibold">
                                CURRENT FEE
                            </span>
                            <div className="flex items-center gap-x-2">
                                {groupedHighlightedFees.map((group) => (
                                    <div
                                        key={group.type}
                                        className="flex items-center gap-x-1"
                                    >
                                        <span className="text-caption font-regular uppercase">
                                            {group.label}:
                                        </span>
                                        <span className="text-caption font-semibold flex items-center gap-x-1">
                                            {group.entries.map((entry, i) => {
                                                const showTooltip = Boolean(
                                                    entry.feesCalculated
                                                        ?.isTbd ||
                                                        entry.feesCalculated
                                                            ?.evalFee ||
                                                        entry.feesCalculated
                                                            ?.udmFee
                                                );

                                                return (
                                                    <span
                                                        key={`${group.type} ${
                                                            entry.value
                                                                ? `-${entry.value}`
                                                                : ""
                                                        }`}
                                                        className="flex items-center gap-x-1"
                                                    >
                                                        {i > 0 && ", "}
                                                        {entry.value}
                                                        {entry.isOverride &&
                                                            entry.providerName && (
                                                                <span className="text-caption font-regular">
                                                                    (
                                                                    {
                                                                        entry.providerName
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        {showTooltip &&
                                                            entry.value && (
                                                                <ActionButton
                                                                    variant="normal"
                                                                    size="auto"
                                                                    actionTitle={getMessageForFees(
                                                                        entry.value
                                                                    )}
                                                                >
                                                                    <InfoIcon className="stroke-textSec" />
                                                                </ActionButton>
                                                            )}
                                                    </span>
                                                );
                                            })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
            </div>
            {showInsuranceDetails && record.name !== "Self-Pay" && (
                <div className="flex flex-col gap-y-[--spacing-items]">
                    <div className="flex items-center justify-between px-[--padding-card-x]">
                        <p className="text-title text-primary font-semibold uppercase">
                            Insurance Details
                        </p>
                        <Button
                            onClick={() =>
                                setFormInView(FormTypes.EDIT_INSURANCE)
                            }
                        >
                            Edit
                        </Button>
                    </div>
                    <Card classNames="w-full text-base font-regular items-center text-dark">
                        <div className="grid grid-cols-5 gap-x-2 gap-y-5 ">
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    PROVIDER&rsquo;s NAME
                                </span>
                                <div className="text-caption font-regular">
                                    {record.name ? record.name : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Is Cal Year
                                </span>
                                <div className="text-caption font-regular">
                                    {record.is_cal_year ? "Yes" : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Effective start date
                                </span>
                                <div className="text-caption font-regular">
                                    {record.effective_start_date
                                        ? formatDateString(
                                              record.effective_start_date
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Effective end date
                                </span>
                                <div className="text-caption font-regular">
                                    {record.effective_end_date
                                        ? formatDateString(
                                              record.effective_end_date
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Insurance type
                                </span>
                                <div className="text-caption font-regular">
                                    {record.insurance_type
                                        ? removeEnumUnderscore(
                                              record.insurance_type
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Employer name
                                </span>
                                <div className="text-caption font-regular">
                                    {record.employer_name
                                        ? record.employer_name
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Insurance member id
                                </span>
                                <div className="text-caption font-regular">
                                    {record.insurance_member_id
                                        ? record.insurance_member_id
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Status
                                </span>
                                <div className="text-caption font-regular">
                                    {record.status ? record.status : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Insurance accepted
                                </span>
                                <div className="text-caption font-regular">
                                    {record.insurance_accepted ? "Yes" : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Non-client primary insured name
                                </span>
                                <div className="text-caption font-regular">
                                    {record.non_client_primary_insured_name
                                        ? record.non_client_primary_insured_name
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Non-client primary insured dob
                                </span>
                                <div className="text-caption font-regular">
                                    {record.non_client_primary_insured_dob
                                        ? formatDateString(
                                              record.non_client_primary_insured_dob
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Primary insured address
                                </span>
                                <div className="text-caption font-regular">
                                    {record.primary_insured_address
                                        ? record.primary_insured_address
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Primary insured city
                                </span>
                                <div className="text-caption font-regular">
                                    {record.primary_insured_city
                                        ? record.primary_insured_city
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Primary insured state
                                </span>
                                <div className="text-caption font-regular">
                                    {record.primary_insured_state
                                        ? record.primary_insured_state
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Primary insured zipcode
                                </span>
                                <div className="text-caption font-regular">
                                    {record.primary_insured_zipcode
                                        ? record.primary_insured_zipcode
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Primary insured phone no
                                </span>
                                <div className="text-caption font-regular">
                                    {record.primary_insured_phone_no
                                        ? record.primary_insured_phone_no
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Primary insured sex
                                </span>
                                <div className="text-caption font-regular">
                                    {record.primary_insured_sex
                                        ? record.primary_insured_sex
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Primary insured dob
                                </span>
                                <div className="text-caption font-regular">
                                    {record.primary_insured_dob
                                        ? formatDateString(
                                              record.primary_insured_dob
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Relationship to primary insured
                                </span>
                                <div className="text-caption font-regular">
                                    {record.relationship_to_primary_insured
                                        ? record.relationship_to_primary_insured
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Insured policy group
                                </span>
                                <div className="text-caption font-regular">
                                    {record.insured_policy_group
                                        ? record.insured_policy_group
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Financial assistance beneficiary
                                </span>
                                <div className="text-caption font-regular">
                                    {record.financial_assistance_beneficiary
                                        ? record.financial_assistance_beneficiary
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Last verified
                                </span>
                                <div className="text-caption font-regular">
                                    {record.last_verified
                                        ? formatDateString(record.last_verified)
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Accumulation start date
                                </span>
                                <div className="text-caption font-regular">
                                    {record.accumulations_start_date
                                        ? formatDateString(
                                              record.accumulations_start_date
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Accumulation end date
                                </span>
                                <div className="text-caption font-regular">
                                    {record.accumulations_end_date
                                        ? formatDateString(
                                              record.accumulations_end_date
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Policy effective start date
                                </span>
                                <div className="text-caption font-regular">
                                    {record.policy_effective_start_date
                                        ? formatDateString(
                                              record.policy_effective_start_date
                                          )
                                        : "--"}
                                </div>
                            </div>
                            <div className="flex flex-col items-start justify-center gap-y-1">
                                <span className="font-semibold text-caption uppercase">
                                    Policy effective end date
                                </span>
                                <div className="text-caption font-regular">
                                    {record.policy_effective_end_date
                                        ? formatDate(
                                              formatDateString(
                                                  record.policy_effective_end_date
                                              ),
                                              true
                                          )
                                        : "--"}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-y-2 mt-5">
                            <p className="text-base font-semibold uppercase">
                                Attached Files ({record.attached_files.length})
                            </p>
                            <div className="grid grid-cols-[repeat(auto-fit,minmax(262px,1fr))] gap-2">
                                {record.attached_files &&
                                    record.attached_files.map(
                                        (attached_file) => {
                                            return (
                                                <Card
                                                    key={attached_file.id}
                                                    classNames="flex flex-col gap-y-2"
                                                >
                                                    <span className="text-caption font-medium text-left">
                                                        {`${attached_file.name}`}
                                                    </span>
                                                    <div className="flex justify-between items-center font-normal text-caption">
                                                        <Button
                                                            variant="normal"
                                                            size="auto"
                                                            onClick={() => {
                                                                handleDocumentinView(
                                                                    attached_file
                                                                );
                                                            }}
                                                            className="text-textLink underline"
                                                        >
                                                            View doc
                                                        </Button>

                                                        <Button
                                                            variant="can-hover"
                                                            size="auto"
                                                            onClick={() => {
                                                                setShowDeleteConfirmation(
                                                                    true
                                                                );
                                                                setDocumentToDeleteId(
                                                                    attached_file.id
                                                                );
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </Button>
                                                    </div>
                                                </Card>
                                            );
                                        }
                                    )}
                            </div>
                        </div>
                    </Card>
                </div>
            )}
            {showFeesHistory && (
                <div className="flex flex-col gap-y-[--spacing-items]">
                    <div className="flex items-center justify-between px-[--padding-card-x]">
                        <p className="text-title text-primary font-semibold uppercase">
                            Fees History
                        </p>
                        {record.fees.length > 0 && (
                            <Button
                                onClick={() => setShowEditFeesDialog(true)}
                                disabled={isEditFeeMutating > 0}
                            >
                                Edit fees
                            </Button>
                        )}
                    </div>
                    {record.fees.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            {record.fees.map((fee, i) => (
                                <ClientSingleFee
                                    defaultIsMaximized={i === 0}
                                    key={fee.fee_id}
                                    fee={fee}
                                    isCurrentFee={i === 0}
                                />
                            ))}
                        </div>
                    ) : (
                        <ListState
                            isError={false}
                            emptyMessage="No fees found"
                            stateHelperText="Add fee for this client to view fees history"
                        />
                    )}
                </div>
            )}
        </div>
    );
}
